import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Check } from "@phosphor-icons/react";

const ACCENTS = [
  { name: "Crimson", hex: "#D32F2F" },
  { name: "Ink Black", hex: "#111111" },
  { name: "Forest", hex: "#1F5F4A" },
  { name: "Cobalt", hex: "#1E40AF" },
  { name: "Marigold", hex: "#C77700" },
  { name: "Plum", hex: "#6B2D5C" },
];

const FONTS = [
  { id: "editorial", label: "Editorial", sub: "Playfair Display + IBM Plex Sans" },
  { id: "modern", label: "Modern", sub: "IBM Plex Sans only" },
  { id: "mono", label: "Manuscript", sub: "IBM Plex Mono headings" },
];

export default function ThemeSettings() {
  const { user, refreshMe } = useAuth();
  const [theme, setTheme] = useState(user?.theme || { mode: "light", accent: "#D32F2F", font: "editorial" });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");

  const save = async () => {
    setBusy(true); setOk("");
    await api.put("/profile/theme", theme);
    await refreshMe();
    setOk("Theme saved.");
    setBusy(false);
    setTimeout(() => setOk(""), 2000);
  };

  return (
    <div>
      <div className="mb-8">
        <div className="eyebrow text-muted-foreground mb-2">Look &amp; feel</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Theme</h1>
        <p className="mt-2 text-sm text-muted-foreground">Customize how readers experience your site.</p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-7 space-y-8">
          <div>
            <Label className="eyebrow text-muted-foreground">Site mode</Label>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {["light", "dark"].map((m) => (
                <button key={m} type="button" onClick={() => setTheme({ ...theme, mode: m })}
                  className={`p-5 border text-left transition-colors ${theme.mode === m ? "border-foreground bg-secondary" : "border-border hover:bg-secondary/50"}`}
                  data-testid={`theme-mode-${m}`}
                >
                  <div className="eyebrow text-muted-foreground">{m}</div>
                  <div className="font-serif text-2xl mt-1 capitalize">{m === "light" ? "Daylight" : "Night desk"}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground">Accent color</Label>
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3">
              {ACCENTS.map((a) => (
                <button key={a.hex} type="button" onClick={() => setTheme({ ...theme, accent: a.hex })}
                  className={`relative h-16 border ${theme.accent === a.hex ? "border-foreground" : "border-border"}`}
                  style={{ background: a.hex }}
                  aria-label={a.name}
                  data-testid={`theme-accent-${a.name.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  {theme.accent === a.hex && <Check size={14} className="absolute top-1 right-1 text-white" weight="bold" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground">Typography</Label>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FONTS.map((f) => (
                <button key={f.id} type="button" onClick={() => setTheme({ ...theme, font: f.id })}
                  className={`p-4 border text-left ${theme.font === f.id ? "border-foreground bg-secondary" : "border-border hover:bg-secondary/50"}`}
                  data-testid={`theme-font-${f.id}`}
                >
                  <div className={`text-2xl ${f.id === "mono" ? "font-mono" : "font-serif"}`}>{f.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{f.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={busy} className="rounded-none bg-foreground text-background hover:bg-foreground/90" data-testid="theme-save-button">
              {busy ? "Saving..." : "Save theme"}
            </Button>
            {ok ? <span className="text-sm text-emerald-700 dark:text-emerald-400">{ok}</span> : null}
          </div>
        </div>

        <aside className="col-span-12 lg:col-span-5">
          <div className="border border-border bg-card overflow-hidden" data-testid="theme-preview">
            <div className="p-5 border-b border-border" style={{ borderColor: theme.accent }}>
              <div className="eyebrow text-muted-foreground">Preview</div>
              <div className={`text-3xl mt-1 ${theme.font === "mono" ? "font-mono" : "font-serif"} font-black`}>{user?.site_title || "Your Journal"}</div>
              <div className="text-sm text-muted-foreground">{user?.site_tagline}</div>
            </div>
            <div className="p-5">
              <div className="eyebrow mb-2" style={{ color: theme.accent }}>Featured</div>
              <div className={`text-2xl ${theme.font === "mono" ? "font-mono" : "font-serif"} font-bold`}>The art of starting again</div>
              <p className="mt-2 text-sm text-muted-foreground">Sometimes the only way forward is to delete the second draft and look out the window.</p>
              <div className="mt-3 inline-block px-3 py-1 text-xs text-white" style={{ background: theme.accent }}>Read essay</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
